import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar, Clock, MapPin, MapPinned, Phone, Send, Star, FileText, X,
  ChevronDown, UserCheck, UserX, Check, CalendarCheck, ExternalLink,
  Video as VideoIcon, Plus, Search, Shield, Users, ClipboardList, LogOut
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEPARTMENT_LABELS = {
  chefsTable: { label: "Chef's Table" },
  cakeStudio: { label: "Cake Studio" },
  marketing: { label: "Marketing" }
};

const ENTITY_CONFIG = {
  roch_ramen: { label: "ROCH Ramen", color: "text-red-400 bg-red-500/20 border-red-500/30" },
  roch_chefs_table: { label: "Chef's Table", color: "text-amber-400 bg-amber-500/20 border-amber-500/30" },
  roch_cake_studio: { label: "Cake Studio", color: "text-pink-400 bg-pink-500/20 border-pink-500/30" },
  central_team: { label: "Central", color: "text-slate-400 bg-slate-500/20 border-slate-500/30" },
  legacy: { label: "Legacy", color: "text-gray-400 bg-gray-500/20 border-gray-500/30" },
};

const VALID_STATUSES = [
  "applied", "under_review", "shortlisted", "interview_scheduled",
  "interviewed", "trial_scheduled", "trial_completed", "offer_made",
  "hired", "on_hold", "rejected", "no_show"
];

const STATUS_LABELS = {
  applied: "Applied", under_review: "Under Review", shortlisted: "Shortlisted",
  interview_scheduled: "Interview Scheduled", interviewed: "Interviewed",
  trial_scheduled: "Trial Scheduled", trial_completed: "Trial Completed",
  offer_made: "Offer Made", hired: "Hired", on_hold: "On Hold",
  rejected: "Rejected", no_show: "No Show"
};

const WHATSAPP_TEMPLATES = {
  confirmation: (name, role) => `*ROCH Hospitality - Application Received*\n\nDear ${name},\n\nThank you for applying for the *${role}* position.\n\nWe have received your application and will review it shortly.\n\nBest regards,\n*ROCH Hospitality Team*`,
  interviewScheduled: (name, role, date, time, type, link) => {
    const loc = type === "video_call" ? `*Meeting Link:* ${link || '[Link will be shared]'}` : `*Location:* ROCH Hospitality, Kondapur, Hyderabad`;
    return `*ROCH Hospitality - Interview Scheduled*\n\nDear ${name},\n\nYou've been shortlisted for *${role}*.\n\n*Date:* ${date}\n*Time:* ${time}\n*Type:* ${type === "video_call" ? "Video Call" : "In-Person"}\n${loc}\n\nBest regards,\n*ROCH Hospitality Team*`;
  },
  reminder24h: (name, role, date, time, type, link) => `*Interview Reminder*\n\nDear ${name},\n\nReminder: Your interview for *${role}* is tomorrow.\n\n*Date:* ${date}\n*Time:* ${time}\n${type === "video_call" ? `*Link:* ${link || '[Link will be shared]'}` : '*Location:* ROCH Hospitality, Kondapur'}\n\nBest of luck!\n*ROCH Hospitality Team*`,
  reminder1h: (name, role, time, type, link) => `*Interview in 1 Hour*\n\nDear ${name},\n\nYour interview for *${role}* starts at *${time}*.\n\n${type === "video_call" ? `*Link:* ${link || '[Link]'}` : '*Location:* ROCH Hospitality, Kondapur'}\n\nAll the best!\n*ROCH Hospitality Team*`,
  interviewed: (name) => `Dear ${name},\n\nThank you for taking the time to interview with us at ROCH Hospitality.\n\nWe will be in touch regarding next steps.\n\n*ROCH Hospitality Team*`,
  hired: (name, role) => `*Congratulations ${name}!*\n\nWe are pleased to offer you the *${role}* position at ROCH Hospitality.\n\nWe will share further details shortly.\n\n*ROCH Hospitality Team*`,
  rejected: (name) => `Dear ${name},\n\nThank you for your interest in ROCH Hospitality.\n\nAfter careful consideration, we've decided to move forward with other candidates.\n\nWe wish you the best.\n\n*ROCH Hospitality Team*`,
};

const STATUS_COLORS = {
  applied: { bg: "bg-blue-500/20", text: "text-blue-400", label: "Applied" },
  interview_scheduled: { bg: "bg-amber-500/20", text: "text-amber-400", label: "Interview Scheduled" },
  interviewed: { bg: "bg-purple-500/20", text: "text-purple-400", label: "Interviewed" },
  hired: { bg: "bg-green-500/20", text: "text-green-400", label: "Hired" },
  rejected: { bg: "bg-red-500/20", text: "text-red-400", label: "Rejected" },
  pending: { bg: "bg-gray-500/20", text: "text-gray-400", label: "Pending" }
};

const ROLE_LABELS = { super_admin: "Super Admin", hr: "HR", sous_chef: "Sous Chef" };
const ROLE_COLORS = { super_admin: "text-amber-400 bg-amber-500/20", hr: "text-blue-400 bg-blue-500/20", sous_chef: "text-green-400 bg-green-500/20" };

// Auth helpers - use localStorage + Authorization header (cookies unreliable through proxies)
const getAuthHeader = () => {
  const token = localStorage.getItem("roch_session_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function AdminPage() {
  const location = useLocation();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(location.state?.user || null);
  const [authChecked, setAuthChecked] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [schedulingApp, setSchedulingApp] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({ scheduled_date: "", scheduled_time: "", interview_type: "office", meeting_link: "", notes: "" });
  const [isScheduling, setIsScheduling] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterEntity, setFilterEntity] = useState("all");
  const [adminTab, setAdminTab] = useState("applications");
  const [allRoles, setAllRoles] = useState([]);
  const [showAddRole, setShowAddRole] = useState(false);
  const [newRole, setNewRole] = useState({ title: "", subtitle: "", salary: "", entity: "roch_chefs_table", department: "Kitchen", location: "Kondapur", responsibilities: "", featured: false });
  const [searchQuery, setSearchQuery] = useState("");
  const [adminUsers, setAdminUsers] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", role: "hr" });
  const [auditLogs, setAuditLogs] = useState([]);
  const [appNotes, setAppNotes] = useState({});
  const [newNote, setNewNote] = useState("");
  const [notesAppId, setNotesAppId] = useState(null);
  const navigate = useNavigate();

  const isSuperAdmin = currentUser?.role === "super_admin";
  const canDelete = ["super_admin", "hr"].includes(currentUser?.role);

  // Auth check on mount - if user passed from AuthCallback, skip auth check
  useEffect(() => {
    if (location.state?.user) {
      // User already authenticated via AuthCallback
      loadAllData();
      return;
    }
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh applications every 30 seconds so updates from other users are visible
  useEffect(() => {
    if (!currentUser) return;
    const interval = setInterval(() => { fetchApplications(); }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem("roch_session_token");
      if (!token) { setAuthChecked(true); setLoading(false); return; }
      const resp = await axios.get(`${API}/auth/me`, { headers: getAuthHeader() });
      setCurrentUser(resp.data);
      loadAllData();
    } catch {
      localStorage.removeItem("roch_session_token");
      setAuthChecked(true);
      setLoading(false);
    }
  };

  const loadAllData = () => { fetchApplications(); fetchRoles(); };

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { headers: getAuthHeader() }).catch(() => {});
    localStorage.removeItem("roch_session_token");
    setCurrentUser(null);
    setAuthChecked(true);
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Data fetching
  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API}/applications`, { headers: getAuthHeader() });
      setApplications(response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      if (error.response?.status === 401) { setCurrentUser(null); setAuthChecked(true); }
    } finally { setLoading(false); }
  };

  const fetchRoles = async () => {
    try { const r = await axios.get(`${API}/roles?active_only=false`); setAllRoles(r.data); } catch {}
  };

  const fetchAdminUsers = async () => {
    try { const r = await axios.get(`${API}/admin/users`, { headers: getAuthHeader() }); setAdminUsers(r.data); } catch {}
  };

  const fetchAuditLogs = async () => {
    try { const r = await axios.get(`${API}/admin/audit-logs`, { headers: getAuthHeader() }); setAuditLogs(r.data); } catch {}
  };

  const fetchNotes = async (appId) => {
    try { const r = await axios.get(`${API}/applications/${appId}/notes`, { headers: getAuthHeader() }); setAppNotes(prev => ({ ...prev, [appId]: r.data })); } catch {}
  };

  const addNote = async (appId) => {
    if (!newNote.trim()) return;
    try {
      await axios.post(`${API}/applications/${appId}/notes`, { content: newNote }, { headers: getAuthHeader() });
      setNewNote("");
      fetchNotes(appId);
      toast.success("Note added");
    } catch { toast.error("Failed"); }
  };

  // Application actions
  const updateApplicationStatus = async (appId, status, e) => {
    e.stopPropagation();
    try { await axios.patch(`${API}/applications/${appId}`, { status }, { headers: getAuthHeader() }); toast.success("Status updated"); fetchApplications(); } catch { toast.error("Failed"); }
  };

  const deleteApplication = async (appId, name, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete application from ${name}?`)) return;
    try { await axios.delete(`${API}/applications/${appId}`, { headers: getAuthHeader() }); toast.success("Deleted"); setSelectedApp(null); fetchApplications(); } catch { toast.error("Failed"); }
  };

  const assignApplication = async (appId, userId) => {
    try { await axios.patch(`${API}/applications/${appId}`, { assigned_to: userId || "" }, { headers: getAuthHeader() }); toast.success("Assigned"); fetchApplications(); } catch { toast.error("Failed"); }
  };

  const openScheduleModal = (app, e) => {
    e.stopPropagation();
    setSchedulingApp(app);
    setScheduleForm({ scheduled_date: app.interview?.scheduled_date || "", scheduled_time: app.interview?.scheduled_time || "", interview_type: app.interview?.interview_type || "office", meeting_link: app.interview?.meeting_link || "", notes: app.interview?.notes || "" });
    setShowScheduleModal(true);
  };

  const handleScheduleInterview = async (e) => {
    e.preventDefault();
    if (!scheduleForm.scheduled_date || !scheduleForm.scheduled_time) { toast.error("Select date and time"); return; }
    setIsScheduling(true);
    try {
      await axios.post(`${API}/applications/${schedulingApp.id}/schedule-interview`, scheduleForm, { headers: getAuthHeader() });
      toast.success("Interview scheduled!");
      setShowScheduleModal(false);
      const d = new Date(scheduleForm.scheduled_date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      const msg = WHATSAPP_TEMPLATES.interviewScheduled(schedulingApp.full_name, schedulingApp.role, d, scheduleForm.scheduled_time, scheduleForm.interview_type, scheduleForm.meeting_link || null);
      openWhatsApp(schedulingApp.whatsapp_number, msg);
      fetchApplications();
    } catch { toast.error("Failed"); } finally { setIsScheduling(false); }
  };

  const openWhatsApp = (phone, message) => {
    window.open(`https://wa.me/${phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const sendWhatsApp = (app, type, e) => {
    if (e) e.stopPropagation();
    let msg = "";
    const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    switch (type) {
      case "confirmation": msg = WHATSAPP_TEMPLATES.confirmation(app.full_name, app.role); break;
      case "interview": if (app.interview?.scheduled_date) msg = WHATSAPP_TEMPLATES.interviewScheduled(app.full_name, app.role, fmtDate(app.interview.scheduled_date), app.interview.scheduled_time, app.interview.interview_type, app.interview.meeting_link); break;
      case "reminder24h": if (app.interview?.scheduled_date) msg = WHATSAPP_TEMPLATES.reminder24h(app.full_name, app.role, fmtDate(app.interview.scheduled_date), app.interview.scheduled_time, app.interview.interview_type, app.interview.meeting_link); break;
      case "reminder1h": if (app.interview?.scheduled_time) msg = WHATSAPP_TEMPLATES.reminder1h(app.full_name, app.role, app.interview.scheduled_time, app.interview.interview_type, app.interview.meeting_link); break;
      case "interviewed": msg = WHATSAPP_TEMPLATES.interviewed(app.full_name); break;
      case "hired": msg = WHATSAPP_TEMPLATES.hired(app.full_name, app.role); break;
      case "rejected": msg = WHATSAPP_TEMPLATES.rejected(app.full_name); break;
      default: break;
    }
    if (msg) openWhatsApp(app.whatsapp_number, msg);
  };

  // Role management
  const handleAddRole = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/roles`, { ...newRole, responsibilities: newRole.responsibilities.split("\n").map(r => r.trim()).filter(Boolean) });
      toast.success("Role added!"); setShowAddRole(false); setNewRole({ title: "", subtitle: "", salary: "", entity: "roch_chefs_table", department: "Kitchen", location: "Kondapur", responsibilities: "", featured: false }); fetchRoles();
    } catch { toast.error("Failed"); }
  };
  const removeRole = async (id, title) => { if (!window.confirm(`Remove "${title}"?`)) return; try { await axios.delete(`${API}/roles/${id}`); toast.success("Removed"); fetchRoles(); } catch { toast.error("Failed"); } };
  const reactivateRole = async (id, title) => { try { await axios.post(`${API}/roles/${id}/reactivate`); toast.success("Reactivated"); fetchRoles(); } catch { toast.error("Failed"); } };
  const toggleFeatured = async (role) => { try { await axios.patch(`${API}/roles/${role.id}`, { ...role, location: role.location || "Kondapur", featured: !role.featured }); fetchRoles(); } catch {} };

  // User management
  const handleAddUser = async (e) => {
    e.preventDefault();
    try { await axios.post(`${API}/admin/users`, newUser, { headers: getAuthHeader() }); toast.success("User added!"); setShowAddUser(false); setNewUser({ name: "", email: "", role: "hr" }); fetchAdminUsers(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggleUserStatus = async (uid, status) => { try { await axios.patch(`${API}/admin/users/${uid}`, { status: status === "active" ? "inactive" : "active" }, { headers: getAuthHeader() }); fetchAdminUsers(); } catch { toast.error("Failed"); } };
  const deleteUser = async (uid, email) => { if (!window.confirm(`Remove ${email}?`)) return; try { await axios.delete(`${API}/admin/users/${uid}`, { headers: getAuthHeader() }); toast.success("Removed"); fetchAdminUsers(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  // Helpers
  const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return d; } };
  const fmtInterviewDate = (d) => { try { return new Date(d).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }); } catch { return d; } };
  const fmtAuditDate = (d) => { try { return new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return d; } };

  const filteredApplications = applications.filter(app => {
    const matchStatus = filterStatus === "all" || app.status === filterStatus;
    const matchEntity = filterEntity === "all" || app.entity === filterEntity;
    const matchSearch = !searchQuery || [app.full_name, app.email, app.role, app.whatsapp_number].some(f => (f || "").toLowerCase().includes(searchQuery.toLowerCase()));
    return matchStatus && matchEntity && matchSearch;
  });

  const getAssignedName = (uid) => { const u = adminUsers.find(u => u.user_id === uid); return u ? u.name : null; };

  // ==================== RENDER ====================

  // Login screen
  if (!currentUser && authChecked) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4" data-testid="admin-login">
        <div className="bg-[#121212] border border-[#1f1f1f] p-8 max-w-md w-full rounded-2xl text-center">
          <h1 className="font-heading text-2xl text-[#EDEDED] mb-2">ROCH Hospitality</h1>
          <p className="text-[#525252] text-sm mb-8">Admin Dashboard</p>
          <button onClick={handleGoogleLogin} className="w-full flex items-center justify-center gap-3 bg-white text-[#333] py-3 px-6 rounded-xl font-medium hover:bg-gray-100 transition-colors" data-testid="google-login-btn">
            <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#4285F4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#34A853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 010-9.18l-7.98-6.19a24.03 24.03 0 000 21.56l7.98-6.19z"/><path fill="#EA4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            Sign in with Google
          </button>
          <p className="text-[#525252] text-xs mt-6">Only approved Gmail accounts can access the admin panel.</p>
        </div>
      </div>
    );
  }

  // Loading
  if (!currentUser) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-[#D97706] border-t-transparent rounded-full" /></div>;

  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl text-[#EDEDED]">ROCH Admin</h1>
            <div className="flex items-center gap-2 mt-1">
              {currentUser.picture && <img src={currentUser.picture} alt="" className="w-6 h-6 rounded-full" />}
              <span className="text-sm text-[#A1A1AA]">{currentUser.name}</span>
              <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-lg ${ROLE_COLORS[currentUser.role] || "text-gray-400 bg-gray-500/20"}`}>{ROLE_LABELS[currentUser.role]}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" className="btn-secondary text-sm py-2 px-4 rounded-xl">Back to Site</a>
            <button onClick={handleLogout} className="flex items-center gap-1 text-sm text-[#525252] hover:text-[#A1A1AA] py-2 px-3 transition-colors"><LogOut className="w-4 h-4" />Logout</button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="flex gap-1 border-b border-[#1f1f1f] overflow-x-auto">
          {[
            { key: "applications", label: `Applications (${applications.length})`, show: true },
            { key: "roles", label: `Roles (${allRoles.filter(r => r.active).length})`, show: isSuperAdmin },
            { key: "team", label: "Team Access", show: isSuperAdmin, icon: Users, onClick: fetchAdminUsers },
            { key: "audit", label: "Audit Log", show: isSuperAdmin, icon: ClipboardList, onClick: fetchAuditLogs },
          ].filter(t => t.show).map(tab => (
            <button key={tab.key} onClick={() => { setAdminTab(tab.key); tab.onClick?.(); }}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${adminTab === tab.key ? "border-[#D97706] text-[#F59E0B]" : "border-transparent text-[#525252] hover:text-[#A1A1AA]"}`}
              data-testid={`tab-${tab.key}`}>
              {tab.icon && <tab.icon className="w-4 h-4 inline mr-1" />}{tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ===== APPLICATIONS TAB ===== */}
      {adminTab === "applications" && (<>
        {/* Search */}
        <div className="max-w-6xl mx-auto mb-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#525252]" />
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, role or phone..." data-testid="search-applications"
              className="w-full pl-11 pr-4 py-3 bg-[#121212] border border-[#1f1f1f] rounded-xl text-[#EDEDED] placeholder-[#525252] text-sm focus:border-[#D97706]/50 focus:outline-none" />
            {searchQuery && <button onClick={() => setSearchQuery("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#525252] hover:text-[#A1A1AA]"><X className="w-4 h-4" /></button>}
          </div>
        </div>

        {/* Entity Filters */}
        <div className="max-w-6xl mx-auto mb-3 overflow-x-auto">
          <div className="flex gap-2 min-w-max">
            {[{ key: "all", label: "All Entities" }, ...Object.entries(ENTITY_CONFIG).map(([k, v]) => ({ key: k, label: v.label }))].map(f => (
              <button key={f.key} onClick={() => setFilterEntity(f.key)}
                className={`px-4 py-2 text-xs font-medium rounded-lg transition-colors ${filterEntity === f.key ? "bg-[#D97706] text-black" : "bg-[#121212] border border-[#1f1f1f] text-[#525252] hover:text-[#EDEDED]"}`}>
                {f.label} {f.key !== "all" && `(${applications.filter(a => a.entity === f.key).length})`}
              </button>
            ))}
          </div>
        </div>

        {/* Status Filters */}
        <div className="max-w-6xl mx-auto mb-6 overflow-x-auto">
          <div className="flex gap-2 min-w-max">
            {[{ key: "all", label: "All" }, ...Object.entries(STATUS_COLORS).map(([k, v]) => ({ key: k, label: v.label }))].map(f => (
              <button key={f.key} onClick={() => setFilterStatus(f.key)}
                className={`px-4 py-2 text-xs font-medium rounded-lg transition-colors ${filterStatus === f.key ? "bg-[#D97706] text-black" : "bg-[#121212] border border-[#1f1f1f] text-[#525252] hover:text-[#EDEDED]"}`}>
                {f.label} {f.key !== "all" && `(${applications.filter(a => a.status === f.key).length})`}
              </button>
            ))}
          </div>
        </div>

        {/* Applications List */}
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-2 border-[#D97706] border-t-transparent rounded-full" /></div>
          ) : filteredApplications.length === 0 ? (
            <div className="bg-[#121212] border border-[#1f1f1f] rounded-xl p-12 text-center"><p className="text-[#525252]">No applications found.</p></div>
          ) : (
            <div className="space-y-4">
              {filteredApplications.map((app, index) => (
                <div key={app.id} className="bg-[#121212] border border-[#1f1f1f] rounded-xl hover:border-[#D97706]/50 transition-colors" data-testid={`application-${index}`}>
                  {/* Card Header */}
                  <div className="p-4 md:p-6 cursor-pointer" onClick={() => { setSelectedApp(selectedApp?.id === app.id ? null : app); if (selectedApp?.id !== app.id) { fetchNotes(app.id); if (isSuperAdmin && adminUsers.length === 0) fetchAdminUsers(); } }}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="font-heading text-lg text-[#EDEDED]">{app.full_name}</h3>
                          {app.entity && ENTITY_CONFIG[app.entity] && (
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-md border ${ENTITY_CONFIG[app.entity].color}`}>
                              {ENTITY_CONFIG[app.entity].label}
                            </span>
                          )}
                          <span className="px-2 py-0.5 bg-[#D97706]/20 text-[#F59E0B] text-xs rounded-lg">{app.role}</span>
                          <span className={`px-2 py-0.5 text-xs rounded-lg ${STATUS_COLORS[app.status]?.bg || ""} ${STATUS_COLORS[app.status]?.text || ""}`}>{STATUS_COLORS[app.status]?.label || app.status}</span>
                          {app.assigned_to && <span className="px-2 py-0.5 text-xs bg-[#1f1f1f] text-[#A1A1AA] rounded-lg">Assigned: {getAssignedName(app.assigned_to) || "..."}</span>}
                        </div>
                        <p className="text-[#525252] text-sm">{app.email} &middot; {app.whatsapp_number}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-[#525252] text-xs">{fmtDate(app.created_at)}</p>
                          <p className="text-[#A1A1AA] text-sm">{app.years_experience} yrs exp</p>
                        </div>
                        {app.notes_count > 0 && <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-lg">{app.notes_count} notes</span>}
                        <ChevronDown className={`w-5 h-5 text-[#525252] transition-transform ${selectedApp?.id === app.id ? 'rotate-180' : ''}`} />
                      </div>
                    </div>
                  </div>

                  {/* Interview Details */}
                  {app.interview?.scheduled_date && (
                    <div className="mx-4 md:mx-6 mb-3 p-3 bg-[#0A0A0A] border border-[#D97706]/30 rounded-xl">
                      <p className="text-[9px] tracking-[0.2em] uppercase text-[#F59E0B] mb-2 font-semibold">Scheduled Interview</p>
                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        <span className="flex items-center gap-1 text-[#EDEDED]"><Calendar className="w-4 h-4 text-[#F59E0B]" />{fmtInterviewDate(app.interview.scheduled_date)}</span>
                        <span className="flex items-center gap-1 text-[#EDEDED]"><Clock className="w-4 h-4 text-[#F59E0B]" />{app.interview.scheduled_time}</span>
                        <span className="flex items-center gap-1 text-[#EDEDED]">{app.interview.interview_type === "video_call" ? <VideoIcon className="w-4 h-4 text-[#F59E0B]" /> : <MapPinned className="w-4 h-4 text-[#F59E0B]" />}{app.interview.interview_type === "video_call" ? "Video Call" : "In-Person"}</span>
                        {app.interview.meeting_link && <a href={app.interview.meeting_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#F59E0B] hover:underline" onClick={e => e.stopPropagation()}><ExternalLink className="w-3 h-3" />Link</a>}
                      </div>
                    </div>
                  )}

                  {/* WhatsApp Quick Actions */}
                  <div className="px-4 md:px-6 pb-4 flex flex-wrap items-center gap-2">
                    <span className="text-[10px] tracking-wider uppercase text-[#525252] mr-1 flex items-center gap-1"><Phone className="w-3 h-3" />WhatsApp:</span>
                    <button onClick={(e) => { e.stopPropagation(); sendWhatsApp(app, "confirmation", e); }} className="text-xs py-1 px-2.5 bg-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/30 rounded-lg flex items-center gap-1"><Check className="w-3 h-3" />Received</button>
                    {app.interview?.scheduled_date && (<>
                      <button onClick={(e) => sendWhatsApp(app, "interview", e)} className="text-xs py-1 px-2.5 bg-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/30 rounded-lg flex items-center gap-1"><Calendar className="w-3 h-3" />Interview</button>
                      <button onClick={(e) => sendWhatsApp(app, "reminder24h", e)} className="text-xs py-1 px-2.5 bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 rounded-lg flex items-center gap-1"><Clock className="w-3 h-3" />24h</button>
                      <button onClick={(e) => sendWhatsApp(app, "reminder1h", e)} className="text-xs py-1 px-2.5 bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 rounded-lg flex items-center gap-1"><Clock className="w-3 h-3" />1h</button>
                    </>)}
                    {app.status === "hired" && <button onClick={(e) => sendWhatsApp(app, "hired", e)} className="text-xs py-1 px-2.5 bg-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/30 rounded-lg flex items-center gap-1"><Star className="w-3 h-3" />Congrats</button>}
                    {app.status === "rejected" && <button onClick={(e) => sendWhatsApp(app, "rejected", e)} className="text-xs py-1 px-2.5 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg flex items-center gap-1"><X className="w-3 h-3" />Rejection</button>}
                  </div>

                  {/* Expanded Details */}
                  {selectedApp?.id === app.id && (
                    <div className="px-4 md:px-6 pb-4 pt-3 border-t border-[#1f1f1f] space-y-4">
                      <div>
                        <p className="text-xs tracking-[0.15em] uppercase text-[#525252] mb-1">Why should we select you?</p>
                        <p className="text-[#A1A1AA] text-sm leading-relaxed">{app.why_select_you}</p>
                      </div>
                      {app.portfolio_link && <div><p className="text-xs tracking-[0.15em] uppercase text-[#525252] mb-1">Portfolio</p><a href={app.portfolio_link} target="_blank" rel="noopener noreferrer" className="text-[#F59E0B] text-sm hover:underline" onClick={e => e.stopPropagation()}>{app.portfolio_link}</a></div>}
                      {app.cv_filename && <div><p className="text-xs tracking-[0.15em] uppercase text-[#525252] mb-1">CV</p><a href={`${API}/applications/${app.id}/cv`} target="_blank" rel="noopener noreferrer" className="text-[#F59E0B] text-sm hover:underline flex items-center gap-2" onClick={e => e.stopPropagation()}><FileText className="w-4 h-4" />{app.cv_filename}</a></div>}

                      {/* Assign Owner */}
                      {(isSuperAdmin || currentUser?.role === "hr") && adminUsers.length > 0 && (
                        <div>
                          <p className="text-xs tracking-[0.15em] uppercase text-[#525252] mb-1">Assign To</p>
                          <select value={app.assigned_to || ""} onChange={(e) => assignApplication(app.id, e.target.value)} onClick={e => e.stopPropagation()}
                            className="bg-[#0A0A0A] border border-[#1f1f1f] rounded-lg text-sm text-[#EDEDED] py-2 px-3">
                            <option value="">Unassigned</option>
                            {adminUsers.filter(u => u.status === "active").map(u => <option key={u.user_id} value={u.user_id}>{u.name} ({ROLE_LABELS[u.role]})</option>)}
                          </select>
                        </div>
                      )}

                      {/* Notes Timeline */}
                      <div className="pt-3 border-t border-[#1f1f1f]">
                        <p className="text-xs tracking-[0.15em] uppercase text-[#525252] mb-2">Notes & Follow-up</p>
                        <div className="flex gap-2 mb-3" onClick={e => e.stopPropagation()}>
                          <input type="text" value={notesAppId === app.id ? newNote : ""} onFocus={() => setNotesAppId(app.id)}
                            onChange={(e) => { setNotesAppId(app.id); setNewNote(e.target.value); }}
                            placeholder="Add a note..." className="flex-1 bg-[#0A0A0A] border border-[#1f1f1f] rounded-lg text-sm text-[#EDEDED] py-2 px-3 focus:border-[#D97706]/50 focus:outline-none" />
                          <button onClick={() => addNote(app.id)} className="px-3 py-2 bg-[#D97706] text-black text-xs font-medium rounded-lg hover:bg-[#F59E0B] transition-colors">Add</button>
                        </div>
                        {(appNotes[app.id] || []).map(note => (
                          <div key={note.id} className="mb-2 pl-3 border-l-2 border-[#1f1f1f]" onClick={e => e.stopPropagation()}>
                            <div className="flex items-center gap-2 text-xs text-[#525252]">
                              <span className="text-[#A1A1AA] font-medium">{note.user_name}</span>
                              <span className={`px-1.5 py-0.5 rounded text-[9px] ${ROLE_COLORS[note.user_role] || "bg-gray-500/20 text-gray-400"}`}>{ROLE_LABELS[note.user_role] || note.user_role}</span>
                              <span>{fmtAuditDate(note.created_at)}</span>
                            </div>
                            <p className="text-sm text-[#EDEDED] mt-0.5">{note.content}</p>
                          </div>
                        ))}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-2 pt-3 border-t border-[#1f1f1f]">
                        <a href={`mailto:${app.email}`} className="btn-secondary text-xs py-2 px-3 rounded-xl" onClick={e => e.stopPropagation()}><Send className="w-3 h-3 inline mr-1" />Email</a>
                        <button onClick={(e) => openScheduleModal(app, e)} className="btn-secondary text-xs py-2 px-3 rounded-xl"><Calendar className="w-3 h-3 inline mr-1" />{app.interview?.scheduled_date ? "Reschedule" : "Schedule"}</button>
                        {app.cv_filename && <a href={`${API}/applications/${app.id}/cv`} target="_blank" rel="noopener noreferrer" className="btn-secondary text-xs py-2 px-3 rounded-xl" onClick={e => e.stopPropagation()}><FileText className="w-3 h-3 inline mr-1" />CV</a>}
                      </div>

                      {/* Status Buttons */}
                      <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#1f1f1f]">
                        <span className="text-xs text-[#525252] mr-2">Status:</span>
                        {app.status !== "interviewed" && <button onClick={(e) => updateApplicationStatus(app.id, "interviewed", e)} className="text-xs py-1 px-2 bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 rounded-lg">Interviewed</button>}
                        {app.status !== "hired" && <button onClick={(e) => updateApplicationStatus(app.id, "hired", e)} className="text-xs py-1 px-2 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-lg"><UserCheck className="w-3 h-3 inline mr-1" />Hire</button>}
                        {app.status !== "rejected" && <button onClick={(e) => updateApplicationStatus(app.id, "rejected", e)} className="text-xs py-1 px-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg"><UserX className="w-3 h-3 inline mr-1" />Reject</button>}
                        {canDelete && <button onClick={(e) => deleteApplication(app.id, app.full_name, e)} className="text-xs py-1 px-2 bg-red-900/30 text-red-400 hover:bg-red-900/50 rounded-lg ml-auto"><X className="w-3 h-3 inline mr-1" />Delete</button>}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </>)}

      {/* ===== ROLES TAB ===== */}
      {adminTab === "roles" && isSuperAdmin && (
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <p className="text-[#A1A1AA] text-sm">{allRoles.filter(r => r.active).length} active &middot; {allRoles.filter(r => !r.active).length} removed</p>
            <button onClick={() => setShowAddRole(!showAddRole)} className="btn-primary text-sm flex items-center gap-2 rounded-xl"><Plus className="w-4 h-4" />{showAddRole ? "Cancel" : "Add Role"}</button>
          </div>
          {showAddRole && (
            <form onSubmit={handleAddRole} className="bg-[#121212] border border-[#D97706]/30 p-6 mb-6 space-y-4 rounded-xl">
              <h3 className="font-heading text-lg text-[#EDEDED]">Add New Role</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block text-xs uppercase text-[#525252] mb-1">Title *</label><input type="text" value={newRole.title} onChange={e => setNewRole({...newRole, title: e.target.value})} className="input-underline" required /></div>
                <div><label className="block text-xs uppercase text-[#525252] mb-1">Subtitle</label><input type="text" value={newRole.subtitle} onChange={e => setNewRole({...newRole, subtitle: e.target.value})} className="input-underline" /></div>
                <div><label className="block text-xs uppercase text-[#525252] mb-1">Salary *</label><input type="text" value={newRole.salary} onChange={e => setNewRole({...newRole, salary: e.target.value})} className="input-underline" required /></div>
                <div><label className="block text-xs uppercase text-[#525252] mb-1">Entity *</label><select value={newRole.entity} onChange={e => setNewRole({...newRole, entity: e.target.value})} className="input-underline bg-transparent"><option value="roch_chefs_table">Chef's Table</option><option value="roch_cake_studio">Cake Studio</option><option value="roch_ramen">ROCH Ramen</option><option value="central_team">Central Team</option></select></div>
                <div><label className="block text-xs uppercase text-[#525252] mb-1">Department *</label><input type="text" value={newRole.department} onChange={e => setNewRole({...newRole, department: e.target.value})} className="input-underline bg-transparent" placeholder="Kitchen, Service, Production..." /></div>
                <div><label className="block text-xs uppercase text-[#525252] mb-1">Location *</label><select value={newRole.location} onChange={e => setNewRole({...newRole, location: e.target.value})} className="input-underline bg-transparent"><option value="Kondapur">Kondapur</option><option value="Moti Nagar">Moti Nagar</option></select></div>
              </div>
              <div><label className="block text-xs uppercase text-[#525252] mb-1">Responsibilities * (one per line)</label><textarea value={newRole.responsibilities} onChange={e => setNewRole({...newRole, responsibilities: e.target.value})} className="input-underline min-h-[80px] resize-none" required /></div>
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={newRole.featured} onChange={e => setNewRole({...newRole, featured: e.target.checked})} className="accent-[#D97706]" /><span className="text-[#EDEDED] text-sm">Hiring Now (featured)</span></label>
              <button type="submit" className="btn-primary flex items-center gap-2 rounded-xl"><Plus className="w-4 h-4" />Add</button>
            </form>
          )}
          <h3 className="text-[#EDEDED] font-heading text-lg mb-3">Active Roles</h3>
          <div className="space-y-2 mb-8">
            {allRoles.filter(r => r.active).map(role => (
              <div key={role.id} className={`bg-[#121212] border ${role.featured ? 'border-[#D97706]/40' : 'border-[#1f1f1f]'} p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 rounded-xl`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1"><span className="text-[#EDEDED] font-medium">{role.title}</span>{role.subtitle && <span className="text-[#525252] text-xs">({role.subtitle})</span>}{role.featured && <span className="px-1.5 py-0.5 text-[9px] tracking-wider uppercase bg-[#D97706]/20 text-[#F59E0B] border border-[#D97706]/30 rounded">Hiring Now</span>}</div>
                  <p className="text-sm text-[#A1A1AA]">{DEPARTMENT_LABELS[role.department]?.label} &middot; ₹{role.salary}/mo &middot; <MapPin className="w-3 h-3 inline" /> {role.location || "Kondapur"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggleFeatured(role)} className={`text-xs py-1 px-2 rounded-lg ${role.featured ? 'bg-[#D97706]/20 text-[#F59E0B]' : 'bg-[#1f1f1f] text-[#525252]'}`}><Star className="w-3 h-3 inline mr-1" />{role.featured ? "Featured" : "Feature"}</button>
                  <button onClick={() => removeRole(role.id, role.title)} className="text-xs py-1 px-2 bg-red-900/30 text-red-400 hover:bg-red-900/50 rounded-lg"><X className="w-3 h-3 inline mr-1" />Remove</button>
                </div>
              </div>
            ))}
          </div>
          {allRoles.filter(r => !r.active).length > 0 && (<>
            <h3 className="text-[#525252] font-heading text-lg mb-3">Removed Roles</h3>
            <div className="space-y-2">
              {allRoles.filter(r => !r.active).map(role => (
                <div key={role.id} className="bg-[#0A0A0A] border border-[#1f1f1f] p-4 rounded-xl opacity-60 flex justify-between items-center">
                  <div><span className="text-[#A1A1AA]">{role.title}</span><p className="text-xs text-[#525252]">{DEPARTMENT_LABELS[role.department]?.label} &middot; ₹{role.salary}/mo</p></div>
                  <button onClick={() => reactivateRole(role.id, role.title)} className="text-xs py-1 px-3 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-lg flex items-center gap-1"><Plus className="w-3 h-3" />Re-open</button>
                </div>
              ))}
            </div>
          </>)}
        </div>
      )}

      {/* ===== TEAM ACCESS TAB (Super Admin) ===== */}
      {adminTab === "team" && isSuperAdmin && (
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <p className="text-[#A1A1AA] text-sm">{adminUsers.filter(u => u.status === "active").length} active users</p>
            <button onClick={() => setShowAddUser(!showAddUser)} className="btn-primary text-sm flex items-center gap-2 rounded-xl"><Plus className="w-4 h-4" />{showAddUser ? "Cancel" : "Add User"}</button>
          </div>
          {showAddUser && (
            <form onSubmit={handleAddUser} className="bg-[#121212] border border-[#D97706]/30 p-6 mb-6 space-y-4 rounded-xl">
              <h3 className="font-heading text-lg text-[#EDEDED]">Add Team Member</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><label className="block text-xs uppercase text-[#525252] mb-1">Name *</label><input type="text" value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} className="input-underline" required /></div>
                <div><label className="block text-xs uppercase text-[#525252] mb-1">Gmail *</label><input type="email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} className="input-underline" placeholder="name@gmail.com" required /></div>
                <div><label className="block text-xs uppercase text-[#525252] mb-1">Role *</label>
                  <select value={newUser.role} onChange={e => setNewUser({...newUser, role: e.target.value})} className="input-underline bg-transparent">
                    <option value="hr">HR</option>
                    <option value="sous_chef">Sous Chef</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
              </div>
              <button type="submit" className="btn-primary flex items-center gap-2 rounded-xl"><Plus className="w-4 h-4" />Add User</button>
            </form>
          )}
          <div className="space-y-2">
            {adminUsers.map(u => (
              <div key={u.user_id} className={`bg-[#121212] border border-[#1f1f1f] p-4 rounded-xl flex flex-col md:flex-row md:items-center justify-between gap-3 ${u.status !== "active" ? "opacity-50" : ""}`}>
                <div className="flex items-center gap-3">
                  {u.picture ? <img src={u.picture} alt="" className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 rounded-full bg-[#1f1f1f] flex items-center justify-center text-[#525252] text-sm">{u.name?.[0]}</div>}
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-[#EDEDED] font-medium">{u.name}</span>
                      <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-lg ${ROLE_COLORS[u.role] || ""}`}>{ROLE_LABELS[u.role]}</span>
                      {u.status !== "active" && <span className="px-2 py-0.5 text-[10px] bg-red-500/20 text-red-400 rounded-lg">Inactive</span>}
                    </div>
                    <p className="text-sm text-[#525252]">{u.email}</p>
                  </div>
                </div>
                {u.email !== "rohit.chidurala@gmail.com" && (
                  <div className="flex items-center gap-2">
                    <button onClick={() => toggleUserStatus(u.user_id, u.status)} className={`text-xs py-1 px-3 rounded-lg ${u.status === "active" ? "bg-amber-500/20 text-amber-400" : "bg-green-500/20 text-green-400"}`}>{u.status === "active" ? "Deactivate" : "Activate"}</button>
                    <button onClick={() => deleteUser(u.user_id, u.email)} className="text-xs py-1 px-2 bg-red-900/30 text-red-400 hover:bg-red-900/50 rounded-lg"><X className="w-3 h-3" /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== AUDIT LOG TAB (Super Admin) ===== */}
      {adminTab === "audit" && isSuperAdmin && (
        <div className="max-w-6xl mx-auto">
          <div className="space-y-2">
            {auditLogs.length === 0 ? (
              <div className="bg-[#121212] border border-[#1f1f1f] rounded-xl p-12 text-center"><p className="text-[#525252]">No audit logs yet.</p></div>
            ) : auditLogs.map((log, i) => (
              <div key={i} className="bg-[#121212] border border-[#1f1f1f] p-4 rounded-xl flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-[#EDEDED] font-medium">{log.user_name}</span>
                    <span className={`px-1.5 py-0.5 text-[9px] rounded ${ROLE_COLORS[log.user_role] || ""}`}>{ROLE_LABELS[log.user_role]}</span>
                    <span className="text-[#D97706]">{log.action.replace(/_/g, " ")}</span>
                  </div>
                  {log.details && Object.keys(log.details).length > 0 && (
                    <p className="text-xs text-[#525252] mt-1">{JSON.stringify(log.details).slice(0, 120)}</p>
                  )}
                </div>
                <span className="text-xs text-[#525252] whitespace-nowrap">{fmtAuditDate(log.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Schedule Interview Modal */}
      {showScheduleModal && schedulingApp && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowScheduleModal(false)}>
          <div className="bg-[#121212] border border-[#1f1f1f] max-w-md w-full p-6 rounded-2xl relative" onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowScheduleModal(false)} className="absolute top-4 right-4 text-[#525252] hover:text-[#EDEDED]"><X className="w-5 h-5" /></button>
            <h2 className="font-heading text-xl text-[#EDEDED] mb-4">Schedule Interview</h2>
            <p className="text-sm text-[#A1A1AA] mb-6">{schedulingApp.full_name} — {schedulingApp.role}</p>
            <form onSubmit={handleScheduleInterview} className="space-y-4">
              <div><label className="block text-xs uppercase text-[#525252] mb-2">Date *</label><input type="date" value={scheduleForm.scheduled_date} onChange={e => setScheduleForm({...scheduleForm, scheduled_date: e.target.value})} className="input-underline" min={new Date().toISOString().split('T')[0]} required /></div>
              <div><label className="block text-xs uppercase text-[#525252] mb-2">Time *</label><input type="time" value={scheduleForm.scheduled_time} onChange={e => setScheduleForm({...scheduleForm, scheduled_time: e.target.value})} className="input-underline" required /></div>
              <div>
                <label className="block text-xs uppercase text-[#525252] mb-2">Type *</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="type" value="office" checked={scheduleForm.interview_type === "office"} onChange={e => setScheduleForm({...scheduleForm, interview_type: e.target.value})} className="accent-[#D97706]" /><MapPinned className="w-4 h-4 text-[#F59E0B]" /><span className="text-[#EDEDED]">In-Person</span></label>
                  <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="type" value="video_call" checked={scheduleForm.interview_type === "video_call"} onChange={e => setScheduleForm({...scheduleForm, interview_type: e.target.value})} className="accent-[#D97706]" /><VideoIcon className="w-4 h-4 text-[#F59E0B]" /><span className="text-[#EDEDED]">Video Call</span></label>
                </div>
              </div>
              {scheduleForm.interview_type === "video_call" && <div><label className="block text-xs uppercase text-[#525252] mb-2">Meeting Link</label><input type="url" value={scheduleForm.meeting_link} onChange={e => setScheduleForm({...scheduleForm, meeting_link: e.target.value})} className="input-underline" placeholder="Paste link" /></div>}
              <div><label className="block text-xs uppercase text-[#525252] mb-2">Notes</label><textarea value={scheduleForm.notes} onChange={e => setScheduleForm({...scheduleForm, notes: e.target.value})} className="input-underline min-h-[60px] resize-none" /></div>
              <button type="submit" disabled={isScheduling} className="btn-primary w-full flex items-center justify-center gap-2 rounded-xl">
                {isScheduling ? <><span className="animate-spin w-4 h-4 border-2 border-black border-t-transparent rounded-full" />Scheduling...</> : <><CalendarCheck className="w-4 h-4" />Schedule & Notify</>}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
